/**
 * @file AppSidebar — desktop sidebar for professionals.
 * Markup mirrors the design's `cd-sidebar` exactly. Active state and
 * navigation are wired through React Router instead of setPage callbacks.
 */
import React from "react";
import { NavLink, useLocation } from "react-router-dom";

const BrandMark = () => (
  <div className="cd-brand-mark"><span /></div>
);

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const Icon = {
  Home: () => (<svg {...ICON_PROPS}><path d="M3 11l9-8 9 8v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z"/></svg>),
  Calendar: () => (<svg {...ICON_PROPS}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>),
  CalDays: () => (<svg {...ICON_PROPS}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4M8 13h2M14 13h2M8 17h2M14 17h2"/></svg>),
  Users: () => (<svg {...ICON_PROPS}><path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1"/><circle cx="9" cy="7" r="3.5"/><path d="M22 19v-1a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
  User: () => (<svg {...ICON_PROPS}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>),
  Settings: () => (<svg {...ICON_PROPS}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg>),
  Star: () => (<svg {...ICON_PROPS}><path d="M12 3l2.6 5.5 6 .9-4.3 4.3 1 6.1L12 17l-5.3 2.8 1-6.1L3.4 9.4l6-.9z"/></svg>),
  Logout: () => (<svg {...ICON_PROPS}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5M21 12H9"/></svg>),
};

const initials = (first = "", last = "") =>
  `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "U";

function NavItem({ to, end, label, icon, badge, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-label={label}
      onClick={onClick}
      className={({ isActive }) => "cd-nav-item" + (isActive ? " active" : "")}
    >
      {icon}
      <span className="lbl">{label}</span>
      {badge ? <span className="cd-badge">{badge}</span> : null}
    </NavLink>
  );
}

export default function AppSidebar({ user, pendingCount = 0, doctor, onItemClick, onLogout }) {
  const general = [
    { to: "/", end: true, label: "Inicio", icon: <Icon.Home /> },
    { to: "/citas", label: "Citas", icon: <Icon.Calendar />, badge: pendingCount },
    { to: "/calendario", label: "Calendario", icon: <Icon.CalDays /> },
    { to: "/pacientes", label: "Pacientes", icon: <Icon.Users /> },
  ];
  const cuenta = [
    { to: "/mi-perfil", label: "Mi perfil público", icon: <Icon.User /> },
    { to: "/configuracion", label: "Configuración", icon: <Icon.Settings /> },
    { to: "/mi-plan", label: "Mi plan", icon: <Icon.Star /> },
  ];

  return (
    <aside className="cd-sidebar">
      <div className="cd-brand">
        <BrandMark />
        <div className="cd-brand-name">Consultto</div>
      </div>

      <div>
        <div className="cd-nav-label">General</div>
        <nav className="cd-nav">
          {general.map((it) => (
            <NavItem key={it.to} {...it} onClick={onItemClick} />
          ))}
        </nav>
      </div>

      <div>
        <div className="cd-nav-label">Cuenta</div>
        <nav className="cd-nav">
          {cuenta.map((it) => (
            <NavItem key={it.to} {...it} onClick={onItemClick} />
          ))}
        </nav>
      </div>

      <div className="cd-side-foot">
        <a className="cd-doctor-card">
          <div className="cd-doctor-avatar">
            {doctor?.initials || initials(user?.firstName, user?.lastName)}
          </div>
          <div className="cd-doctor-info">
            <div className="n">
              {doctor?.name || [user?.title, user?.firstName, user?.lastName].filter(Boolean).join(" ")}
            </div>
            <div className="s">{doctor?.specialty || user?.email || ""}</div>
          </div>
        </a>
        {onLogout && (
          <button
            type="button"
            className="cd-nav-item"
            style={{ marginTop: 8, color: "var(--ink-3)", width: "100%", background: "transparent", border: 0, cursor: "pointer", font: "inherit" }}
            onClick={onLogout}
          >
            <Icon.Logout />
            <span className="lbl">Cerrar sesión</span>
          </button>
        )}
      </div>
    </aside>
  );
}
